import { useNavigate } from 'react-router-dom';
import * as Styles from './ProductCard.styles.js';
import { Box, Typography } from '@mui/material';
import { FlexContainer } from 'components/styles.js';
import LineClampTypography from 'components/common/ui/LineClampTypography';
import formatPrice from 'src/utils/numbers/formatPrice.js';
import AddToCartButton from '../AddToCartButton.js';
import { getProductMainImage } from 'src/features/products/presentation/utils/getProductMainImage.js';
import useAuthContext from 'src/context/AuthProvider/useAuthContext.js';
import useTrackHomepageEvents from 'src/features/home/presentation/hooks/useTrackHomepageEvents/useTrackHomepageEvents.js';
import { parseToNumberOrNull } from 'src/utils/numbers/parteToNumberOrNull.js';
import { ProductCardProps } from 'src/features/products/presentation/components/ProductCard/ProductCard.types.js';
import { useEffect, useState } from 'react';
import defaultProductImage from 'src/assets/default_product.svg';
import ProductStockMessage from 'src/features/products/presentation/components/ProductCard/ProductStockMessage/ProductStockMessage.js';

const ProductCard = ({
  product,
  width = '100%',
  height = { xs: '210px', sm: '225px', md: '250px' },
  maxTitleLines = 2,
  ...containerProps
}: ProductCardProps) => {
  const { trackProductDetailClick } = useTrackHomepageEvents();
  const [isProductImageError, setIsProductImageError] = useState(false);
  const { isB2b } = useAuthContext();
  const navigate = useNavigate();
  const {
    product: productDetails,
    price,
    discount_price,
    quantity,
    discount_percentage,
    b2b_price,
  } = product;

  const mainImage = getProductMainImage(productDetails);

  useEffect(() => {
    const img = new Image();
    img.src = mainImage;

    img.onload = () => setIsProductImageError(false);
    img.onerror = () => setIsProductImageError(true);
  }, [mainImage]);

  const productPrice = isB2b ? b2b_price : price;
  const discountPrice = isB2b ? null : discount_price;
  const discountPercentage = isB2b ? null : discount_percentage;

  if (
    !productPrice ||
    Number(productPrice) <= 0 ||
    (discountPrice && Number(discountPrice) < 0)
  ) {
    return null;
  }

  const handleProductClick = () => {
    trackProductDetailClick({
      productId: productDetails.pk,
      productName: productDetails.name,
      price: Number(productPrice),
      discountPrice: parseToNumberOrNull(discountPrice),
    });
    navigate(`/products/${productDetails.slug_name}/${productDetails.pk}`);
  };

  const roundedDiscountPercentage = parseToNumberOrNull(discountPercentage)
    ? Math.round(Number(discountPercentage))
    : undefined;

  const [formattedPrice, formattedDiscountPrice] = [
    formatPrice(Number(productPrice), { maximumFractionDigits: 2 }),
    discountPrice && roundedDiscountPercentage
      ? formatPrice(Number(discountPrice), { maximumFractionDigits: 2 })
      : null,
  ];

  const isOutOfStock = !quantity;
  const lowStock = quantity < 4;

  return (
    <Styles.ProductCardWrapper
      width={width}
      height={height}
      onClick={handleProductClick}
      sx={{
        opacity: isOutOfStock ? 0.75 : 1,
      }}
      {...containerProps}
    >
      <Styles.ProductImage
        sx={{
          backgroundImage: `url(${
            isProductImageError ? defaultProductImage : mainImage
          })`,
        }}
      >
        {roundedDiscountPercentage ? (
          <Styles.DiscountPercentageWrapper>
            {roundedDiscountPercentage}%
          </Styles.DiscountPercentageWrapper>
        ) : null}
        <Box mt="auto" px={1.5} pb={1}>
          <AddToCartButton
            variant="icon"
            product={product}
            max={quantity}
            size="small"
            disabled={isOutOfStock}
          />
        </Box>
      </Styles.ProductImage>
      <LineClampTypography
        lines={maxTitleLines}
        content={productDetails.name}
        disableFulltextToggle
        typographyProps={{
          fontWeight: 'bold',
          px: 0.6,
          pt: 0.5,
          minHeight: '20%',
        }}
      />
      {(lowStock || isOutOfStock) && (
        <ProductStockMessage quantity={quantity} />
      )}
      <FlexContainer
        justifyContent="center"
        gap={{ md: 1, sm: 1, xs: 0.5 }}
        pb={1}
        mt="auto"
      >
        <Typography variant="body2" fontWeight="bold">
          {formattedDiscountPrice ?? formattedPrice}
        </Typography>
        {formattedDiscountPrice ? (
          <Typography
            variant="body2"
            color="InactiveCaptionText"
            sx={{ textDecoration: 'line-through' }}
          >
            {formattedPrice}
          </Typography>
        ) : null}
      </FlexContainer>
    </Styles.ProductCardWrapper>
  );
};

export default ProductCard;
